import React from "react";
import { Link } from "react-router-dom";

const expertise = [
  {
    no: "01",
    title: "Payment Processing & FinTech",
    blurb:
      "Designing and operating payment flows across gateways, MIDs and orchestrators — from card capture to settlement.",
    items: [
      "Payment Gateways",
      "Merchant Account Management",
      "MID Routing & Payment Logic",
      "Credit Card Processing",
      "High-Risk Merchant Processing",
      "Payment Orchestration",
      "Transaction Lifecycle Management",
      "Payment APIs & Posting API",
      "Chargeback Prevention (process)",
    ],
  },
  {
    no: "02",
    title: "Subscription & Rebill",
    blurb:
      "Trial-to-rebill funnels, recurring billing schedules and forecast models that keep LTV honest.",
    items: [
      "Subscription Billing",
      "Recurring Payments",
      "Rebill Management",
      "Trial-to-Rebill Flows",
      "Forecast Billing Systems",
      "Subscription Forecast Analysis",
      "Revenue Tracking",
    ],
  },
  {
    no: "03",
    title: "Product & Technical Operations",
    blurb:
      "Bridging product, engineering and merchant ops — turning incidents into specs and specs into shipped features.",
    items: [
      "Requirements Analysis",
      "Feature Specification",
      "Bug Investigation",
      "QA Testing",
      "UAT Testing",
      "Technical Documentation",
      "Incident Investigation",
      "Business Process Analysis",
      "Cross-functional Communication",
      "Product Validation",
    ],
  },
  {
    no: "04",
    title: "Web Development & E-commerce",
    blurb:
      "Storefront clones, vertical landing pages and funnel work on Shopify / WooCommerce with conversion-first instincts.",
    items: [
      "Shopify",
      "WooCommerce",
      "E-commerce Operations",
      "Product Catalog Management",
      "Landing Page Optimization",
      "Conversion Funnel Analysis",
      "Subscription E-commerce",
    ],
  },
  {
    no: "05",
    title: "APIs & Integrations",
    blurb:
      "Connecting payment, billing and 3rd-party services through clean APIs, webhooks and reliable scheduled jobs.",
    items: [
      "REST API",
      "API Testing",
      "Webhooks",
      "Payment API Integrations",
      "Third-Party Service Integrations",
      "Cron Jobs & Scheduled Processes",
    ],
  },
  {
    no: "06",
    title: "Data, Analytics & AI",
    blurb:
      "Reading the numbers behind the dashboard — and using AI workflows to remove the boring half of the work.",
    items: [
      "Transaction Analysis",
      "Payment Reporting",
      "KPI Monitoring",
      "Merchant Performance Analysis",
      "AI-assisted Content Generation",
      "Prompt Engineering",
      "AI Workflow Automation",
      "Automated Content Pipelines",
    ],
  },
];

const stack = [
  "Jira", "Confluence", "Slack", "Telegram",
  "Google Workspace", "Microsoft Office",
  "Shopify", "WooCommerce", "REST APIs",
  "Webhooks", "Cron Jobs", "ChatGPT",
];

const principles = [
  {
    n: "I.",
    title: "Operator, not advisor.",
    text:
      "Twelve years inside payment products — I ship, monitor and unblock, I don't draw slides from the sidelines.",
  },
  {
    n: "II.",
    title: "Numbers before narratives.",
    text:
      "Decisions get tied to transaction volume, decline ratios, rebill survival and chargeback windows — not vibes.",
  },
  {
    n: "III.",
    title: "Boring infrastructure is good infrastructure.",
    text:
      "Predictable routing, documented webhooks, idempotent cron jobs. The exciting part is the merchant winning.",
  },
];

export default function HomePage() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-ink-900/10">
        <div className="wrap grid gap-12 pb-20 pt-16 sm:pt-24 md:grid-cols-12 md:gap-8 md:pb-28">
          <div className="md:col-span-8">
            <div className="reveal flex items-center gap-3" data-delay="1">
              <span className="pill" data-testid="hero-availability">
                <span className="h-1.5 w-1.5 rounded-full bg-clay-500" />
                Available · Q1 2026
              </span>
              <span className="font-mono text-[10.5px] uppercase tracking-[0.22em] text-ink-400">
                Kharkiv, UA → Worldwide
              </span>
            </div>

            <h1
              className="display reveal mt-7 text-[44px] font-medium leading-[0.98] sm:text-[64px] md:text-[78px] lg:text-[92px]"
              data-delay="2"
              data-testid="hero-headline"
            >
              Payment
              <br />
              processing,
              <br />
              <em className="font-normal italic text-clay-600">
                operationally
              </em>{" "}
              done.
            </h1>

            <p
              className="reveal mt-8 max-w-xl text-[17px] leading-relaxed text-ink-700"
              data-delay="3"
              data-testid="hero-subhead"
            >
              I'm <strong className="font-semibold text-ink-900">Roman Ryzhov</strong>,
              an independent FinTech &amp; product operations consultant working under{" "}
              <strong className="font-semibold text-ink-900">VRH Digital</strong>.
              I help merchants and platforms route, rebill and reconcile money —
              cleanly, predictably, and across high-risk verticals.
            </p>

            <div className="reveal mt-9 flex flex-wrap items-center gap-4" data-delay="4">
              <Link
                to="/contact"
                className="btn-primary"
                data-testid="hero-cta-primary"
              >
                Start a project →
              </Link>
              <a
                href="#expertise"
                className="btn-outline"
                data-testid="hero-cta-secondary"
              >
                See what I do
              </a>
            </div>
          </div>

          {/* Identity card — satisfies Payoneer: name + business + email + location */}
          <aside
            className="reveal md:col-span-4"
            data-delay="5"
            data-testid="identity-card"
          >
            <div className="relative border border-ink-900/15 bg-paper p-7">
              <div className="absolute -top-3 left-6 bg-paper px-2 font-mono text-[10px] uppercase tracking-[0.25em] text-ink-500">
                Business profile
              </div>

              <dl className="space-y-5 text-[14.5px]">
                <div>
                  <dt className="label">Operator</dt>
                  <dd className="display text-[22px] font-medium leading-tight text-ink-900">
                    Roman Ryzhov
                  </dd>
                </div>
                <div className="rule" />
                <div>
                  <dt className="label">Business name</dt>
                  <dd className="font-medium text-ink-900">VRH Digital</dd>
                </div>
                <div className="rule" />
                <div>
                  <dt className="label">Line of business</dt>
                  <dd className="text-ink-800">
                    FinTech &amp; Payment Processing — independent consulting
                  </dd>
                </div>
                <div className="rule" />
                <div>
                  <dt className="label">Email</dt>
                  <dd>
                    <a
                      href="mailto:rryzhov@gmail.com"
                      className="link-underline font-medium text-ink-900"
                      data-testid="identity-email"
                    >
                      rryzhov@gmail.com
                    </a>
                  </dd>
                </div>
                <div className="rule" />
                <div>
                  <dt className="label">Website</dt>
                  <dd>
                    <a
                      href="https://vrhdigital.com"
                      className="link-underline font-medium text-ink-900"
                      data-testid="identity-website"
                    >
                      vrhdigital.com
                    </a>
                  </dd>
                </div>
                <div className="rule" />
                <div>
                  <dt className="label">Based in</dt>
                  <dd className="text-ink-800">Kharkiv, Ukraine</dd>
                </div>
                <div className="rule" />
                <div>
                  <dt className="label">Working languages</dt>
                  <dd className="text-ink-800">Ukrainian (native), Russian, English (pro)</dd>
                </div>
              </dl>

              <div className="mt-6 flex items-center justify-between border-t border-ink-900/10 pt-4 font-mono text-[10.5px] uppercase tracking-[0.22em] text-ink-500">
                <span>VRHDIGITAL.COM · 2026</span>
                <span className="text-clay-600">● Active</span>
              </div>
            </div>
          </aside>
        </div>

        {/* Marquee */}
        <div className="border-t border-ink-900/10 bg-paperDeep/60 py-5">
          <div className="marquee-track font-mono text-[11px] uppercase tracking-[0.28em] text-ink-700">
            {[...Array(2)].map((_, dup) => (
              <div key={dup} className="flex items-center gap-16">
                <span>Payment Orchestration</span>
                <span className="text-clay-500">●</span>
                <span>High-Risk Merchants</span>
                <span className="text-clay-500">●</span>
                <span>Subscription & Rebill</span>
                <span className="text-clay-500">●</span>
                <span>API Integrations</span>
                <span className="text-clay-500">●</span>
                <span>Incident Investigation</span>
                <span className="text-clay-500">●</span>
                <span>QA / UAT</span>
                <span className="text-clay-500">●</span>
                <span>E-commerce Operations</span>
                <span className="text-clay-500">●</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section
        id="about"
        className="border-b border-ink-900/10"
        data-testid="about-section"
      >
        <div className="wrap grid gap-12 py-24 md:grid-cols-12">
          <div className="md:col-span-4">
            <p className="eyebrow">§ About</p>
            <h2 className="display mt-4 text-4xl font-medium leading-tight sm:text-5xl">
              An operator
              <br />
              behind the
              <br />
              <em className="font-normal italic text-clay-600">checkout.</em>
            </h2>
          </div>

          <div className="md:col-span-8 md:pl-10">
            <p className="text-[18px] leading-relaxed text-ink-800">
              VRH Digital is a one-person studio run by{" "}
              <strong className="text-ink-900">Roman Ryzhov</strong> out of{" "}
              <strong className="text-ink-900">Kharkiv, Ukraine</strong>. The work
              sits at the intersection of payment processing, product operations
              and the tedious, important glue work that keeps online businesses
              actually getting paid.
            </p>
            <p className="mt-5 text-[18px] leading-relaxed text-ink-800">
              The day-to-day looks like this: investigating why a rebill cohort
              degraded last week, mapping MID routing for a high-risk vertical,
              writing the feature spec the engineers will actually read, sitting
              with QA through UAT, then closing the loop with a clean
              post-mortem.
            </p>
            <p className="mt-5 text-[18px] leading-relaxed text-ink-800">
              No agency layer, no junior account manager — you talk to the
              person doing the work.
            </p>

            <div className="mt-12 grid gap-6 sm:grid-cols-3">
              {[
                { k: "12+ yrs", v: "in payment products" },
                { k: "Multi-vertical", v: "high-risk experience" },
                { k: "Operator", v: "not advisor" },
              ].map((m) => (
                <div
                  key={m.k}
                  className="border-t border-ink-900/15 pt-4"
                  data-testid={`stat-${m.k.replace(/\s/g, "-")}`}
                >
                  <p className="display text-2xl font-medium text-ink-900">
                    {m.k}
                  </p>
                  <p className="mt-1 text-[13.5px] uppercase tracking-wider text-ink-500">
                    {m.v}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="border-b border-ink-900/10 bg-paperDeep/40" data-testid="principles-section">
        <div className="wrap py-24">
          <p className="eyebrow">§ How I work</p>
          <h2 className="display mt-4 max-w-3xl text-4xl font-medium leading-tight sm:text-5xl">
            Three quiet rules that decide
            <em className="font-normal italic text-clay-600"> what gets shipped.</em>
          </h2>

          <div className="mt-14 grid gap-10 md:grid-cols-3">
            {principles.map((p) => (
              <div key={p.n} className="border-t-2 border-ink-900 pt-6">
                <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-clay-600">
                  {p.n}
                </p>
                <h3 className="display mt-3 text-2xl font-medium leading-snug text-ink-900">
                  {p.title}
                </h3>
                <p className="mt-3 text-[15.5px] leading-relaxed text-ink-700">
                  {p.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EXPERTISE */}
      <section
        id="expertise"
        className="border-b border-ink-900/10"
        data-testid="expertise-section"
      >
        <div className="wrap py-24">
          <div className="grid items-end gap-6 md:grid-cols-12">
            <div className="md:col-span-7">
              <p className="eyebrow">§ Expertise</p>
              <h2 className="display mt-4 text-4xl font-medium leading-tight sm:text-5xl">
                Six domains,
                <br />
                one operator.
              </h2>
            </div>
            <p className="text-[15.5px] leading-relaxed text-ink-700 md:col-span-5">
              Each block below maps to live work delivered in production — from
              gateway integrations and rebill flows, to incident reports and
              technical documentation that engineers will actually read.
            </p>
          </div>

          <div className="mt-16 grid gap-px overflow-hidden border border-ink-900/10 bg-ink-900/10 md:grid-cols-2">
            {expertise.map((b) => (
              <article
                key={b.no}
                className="group flex flex-col gap-5 bg-paper p-8 transition hover:bg-paperDeep/70 sm:p-10"
                data-testid={`expertise-${b.no}`}
              >
                <div className="flex items-baseline justify-between">
                  <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink-400">
                    {b.no}
                  </span>
                  <span className="h-px flex-1 mx-4 bg-ink-900/10" />
                  <span className="font-mono text-[10.5px] uppercase tracking-[0.22em] text-clay-600 opacity-0 transition group-hover:opacity-100">
                    Live
                  </span>
                </div>
                <h3 className="display text-[26px] font-medium leading-tight text-ink-900">
                  {b.title}
                </h3>
                <p className="text-[15px] leading-relaxed text-ink-700">
                  {b.blurb}
                </p>
                <ul className="mt-2 flex flex-wrap gap-2">
                  {b.items.map((i) => (
                    <li key={i} className="chip">
                      {i}
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* STACK */}
      <section
        id="stack"
        className="border-b border-ink-900/10"
        data-testid="stack-section"
      >
        <div className="wrap grid gap-12 py-24 md:grid-cols-12">
          <div className="md:col-span-4">
            <p className="eyebrow">§ Stack</p>
            <h2 className="display mt-4 text-4xl font-medium leading-tight sm:text-5xl">
              Tools &amp;<br />
              <em className="font-normal italic text-clay-600">languages.</em>
            </h2>
            <p className="mt-6 text-[15.5px] leading-relaxed text-ink-700">
              Most projects live across these — no insistence on a sacred
              toolchain, only on traceability.
            </p>
          </div>

          <div className="md:col-span-8">
            <div className="grid gap-x-12 gap-y-4 sm:grid-cols-2">
              {stack.map((s, i) => (
                <div
                  key={s}
                  className="flex items-baseline gap-4 border-b border-ink-900/10 py-3"
                  data-testid={`stack-${i}`}
                >
                  <span className="font-mono text-[11px] tracking-[0.18em] text-ink-400">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-[16.5px] text-ink-900">{s}</span>
                </div>
              ))}
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
              <span className="pill-dark">UA — Native</span>
              <span className="pill">RU — Fluent</span>
              <span className="pill">EN — Professional</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-ink-900 text-paper" data-testid="cta-section">
        <div className="wrap grid items-center gap-10 py-20 md:grid-cols-12">
          <div className="md:col-span-8">
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-clay-400">
              § Start a project
            </p>
            <h2 className="display mt-4 text-4xl font-medium leading-tight text-paper sm:text-5xl md:text-6xl">
              Got a payments problem
              <br />
              that needs an{" "}
              <em className="font-normal italic text-clay-400">
                operator
              </em>
              ?
            </h2>
            <p className="mt-5 max-w-xl text-[16px] leading-relaxed text-ink-300">
              Reach out with a short brief — verticals, processors involved,
              current pain. I usually reply within 24 hours, Kyiv time.
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-clay-500 px-7 py-3.5 text-sm font-semibold text-paper transition hover:bg-clay-400"
              data-testid="cta-contact-link"
            >
              Write to Roman →
            </Link>
            <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.22em] text-ink-400">
              rryzhov@gmail.com
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
